import { memo, useState, useCallback, useMemo } from 'react'

import { SMALL_DESKTOP_SIZE_BREAKPOINT } from 'consts'
import { useResponsive } from 'hooks/useResponsive'

import { ColorPickerPopover } from 'common-components/color-picker-popover'

import * as TriggerStyled from './trigger-styled'
import { MobileColorPicker } from './mobile'

interface IColorPickerWithTriggerProps {
  value: string
  onChangeComplete?: (hex: string) => void
  triggerTestId?: string
}

const stripHash = (hex: string) => hex.replace(/^#/, '')

const toCssColor = (hex: string): string =>
  hex.startsWith('#') ? hex : `#${stripHash(hex)}`

const ColorPickerWithTrigger: React.FC<IColorPickerWithTriggerProps> = ({
  value,
  onChangeComplete,
  triggerTestId = 'color-picker-trigger'
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [anchor, setAnchor] = useState<null | HTMLDivElement>(null)
  const isTablet = useResponsive(SMALL_DESKTOP_SIZE_BREAKPOINT)

  const swatchColor = useMemo(() => toCssColor(value || '#000000'), [value])

  const handleClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  const handleToggle = useCallback(() => {
    setIsOpen(open => !open)
  }, [])

  const handleCompleted = useCallback(
    (hex: string) => {
      onChangeComplete?.(hex)
    },
    [onChangeComplete]
  )

  return (
    <>
      <TriggerStyled.Wrapper
        ref={setAnchor}
        role="button"
        tabIndex={0}
        onClick={handleToggle}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleToggle()
          }
        }}
        data-test-id={triggerTestId}
      >
        <TriggerStyled.UiLayer>
          <TriggerStyled.HexLabel>{stripHash(value)}</TriggerStyled.HexLabel>
          <TriggerStyled.SwatchPreview $color={swatchColor} />
        </TriggerStyled.UiLayer>
      </TriggerStyled.Wrapper>

      {isTablet ? (
        <MobileColorPicker
          isOpen={isOpen}
          onClose={handleClose}
          currentColor={value || null}
          onChangeCompleted={handleCompleted}
        />
      ) : (
        <ColorPickerPopover
          isOpen={isOpen}
          onClose={handleClose}
          parentRef={anchor}
          currentColor={value || null}
          onChangeCompleted={handleCompleted}
        />
      )}
    </>
  )
}

export default memo(ColorPickerWithTrigger)
